/**
 * Imperative LIFO registry of open overlays (bottom sheets, dialogs, lightboxes)
 * that should be dismissed by the hardware/gesture back BEFORE any navigation.
 *
 * Imperative (module state, not a store) because only the BackButtonController
 * reads it — imperatively, inside a `popstate`/`navigate` handler — so reactive
 * subscribers would be pure overhead. Overlays opt in via `useBackDismissable`.
 */

export type OverlayEntry = { id: number; onClose: () => void }

let stack: OverlayEntry[] = []
let seq = 0

// Set right before a programmatic `history.back()` (e.g. a sheet closed via
// backdrop/Esc/button) so the controller ignores that one synthetic pop.
let suppressNextPop = false

export function nextOverlayId(): number {
  return ++seq
}

export function registerOverlay(entry: OverlayEntry): void {
  stack.push(entry)
}

export function unregisterOverlay(id: number): void {
  stack = stack.filter((o) => o.id !== id)
}

export function isOverlayRegistered(id: number): boolean {
  return stack.some((o) => o.id === id)
}

export function peekOverlay(): OverlayEntry | null {
  return stack[stack.length - 1] ?? null
}

export function popOverlay(): OverlayEntry | null {
  return stack.pop() ?? null
}

export function getSuppressNextPop(): boolean {
  return suppressNextPop
}

export function setSuppressNextPop(value: boolean): void {
  suppressNextPop = value
}
